import { useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { LuPen, LuTrash2, LuX } from 'react-icons/lu';
import { Chrono } from 'react-chrono';
import 'react-chrono/dist/style.css';
import RelationshipTabs from '../components/RelationshipTabs';
import { useAuth } from '../context/AuthContext';
import { useContact } from '../hooks/useContact';
import { getInteractionEmoji } from '../utils/interactionEmojis';
import { deleteInteraction, updateInteraction } from '../services/interactions';
import { useQueryClient } from '@tanstack/react-query';
import { useIsMobile } from '../hooks/useIsMobile';

const RelationshipInteractionsPage = () => {
  const { id } = useParams();
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const { data: contact, isLoading, isError } = useContact(accessToken, id);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const isMobile = useIsMobile();

  const fullName = contact?.contact
    ? `${contact.contact.first_name || ''} ${contact.contact.last_name || ''}`.trim()
    : '';
  const interactions = useMemo(
    () => (Array.isArray(contact?.interactions) ? [...contact.interactions] : []),
    [contact]
  );

  const timelineItems = useMemo(() => {
    const sorted = [...interactions].sort((a, b) => {
      const da = new Date(a.happened_at || a.created_at || 0).getTime();
      const db = new Date(b.happened_at || b.created_at || 0).getTime();
      return db - da;
    });
    return sorted.map((item) => {
      const dt = item.happened_at ? new Date(item.happened_at) : null;
      const when =
        dt && !Number.isNaN(dt.getTime())
          ? dt.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })
          : 'Time unknown';
      const typeRaw = item.interaction_type;
      const typeLabel = typeRaw.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
      const emoji = getInteractionEmoji(typeRaw);

      return {
        cardTitle: `${emoji} ${typeLabel}`,
        cardSubtitle: when,
        cardSummary: item.summary || 'No summary provided',
        id: item.id,
        interactionType: typeLabel,
        happenedAt: when,
        details: item.details || '',
      };
    });
  }, [interactions]);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="sticky top-0 z-20 border-b border-white/5 bg-[#050815] px-4 pb-3 pt-16 sm:px-6 md:px-8 md:pt-10">
        <RelationshipTabs contactId={id} activeKey="interactions" />
      </div>

      <section className="px-4 py-6 sm:px-6 md:px-8 md:py-10">
        {isLoading && <p className="text-sm text-slate-300">Loading contact…</p>}
        {isError && <p className="text-sm text-amber-300">Unable to load contact.</p>}
        {!isLoading && !isError && (
          <>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-slate-50">Timeline</h2>
              <p className="text-sm text-slate-300">
                Interactions for {fullName || 'this contact'}.
              </p>
            </div>
            {timelineItems.length === 0 ? (
              <p className="text-sm text-slate-400">No interactions recorded yet.</p>
            ) : (
              <div className="w-full">
                <div className="w-full">
                  <Chrono
                    items={timelineItems}
                    mode={isMobile ? 'VERTICAL' : 'VERTICAL_ALTERNATING'}
                    disableToolbar={true}
                    scrollable={false}
                    contentDetailsHeight={200}
                    lineWidth={3}
                    darkMode
                    theme={{ primary: '#38bdf8', secondary: '#0ea5e9', cardBgColor: '#0c1428', titleColor: '#e2e8f0', titleColorActive: '#38bdf8', cardDetailsColor: '#cbd5e1' }}
                    fontSizes={{ cardText: '13px', cardSubtitle: '12px', cardTitle: '15px', title: '12px' }}
                  >
                    {timelineItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex h-[150px] w-full cursor-pointer flex-col"
                        onClick={() => {
                          setSelectedInteraction(item);
                          setEditedSummary(item.summary);
                          setIsEditing(false);
                        }}
                      >
                        <div className="text-base font-semibold text-slate-100">
                          {item.cardSummary}
                        </div>
                        <div className="mt-auto flex flex-col items-end justify-end pt-3">
                          <button
                            type="button"
                            className="rounded-full p-2 text-slate-400 transition hover:bg-white/5 hover:text-rose-400"
                            aria-label="Delete interaction"
                            onClick={() => setPendingDelete(item)}
                          >
                            <LuTrash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </Chrono>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={() => setPendingDelete(null)}>
          <div
            className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0c1428]/95 p-5 shadow-soft"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-slate-50">Delete this interaction?</h3>
            <p className="mt-2 text-sm text-slate-300">This action cannot be undone.</p>
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await deleteInteraction(accessToken, pendingDelete.id);
                    queryClient.invalidateQueries({ queryKey: ['contact', id] });
                    if (selectedInteraction?.id === pendingDelete.id) {
                      setSelectedInteraction(null);
                    }
                  } finally {
                    setPendingDelete(null);
                  }
                }}
                className="rounded-lg bg-rose-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-rose-500/30"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedInteraction && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={() => {
            if (!isSaving) {
              setSelectedInteraction(null);
              setIsEditing(false);
            }
          }}
        >
          <div
            className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0c1428]/95 p-5 shadow-soft"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-50">{selectedInteraction.cardTitle}</h3>
                <p className="text-sm text-slate-300">{selectedInteraction.happenedAt}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-white/10 bg-white/5 p-2 text-slate-200 hover:bg-white/10"
                  aria-label={isEditing ? 'Cancel editing' : 'Edit interaction'}
                  onClick={() => {
                    if (isEditing) {
                      setEditedSummary(selectedInteraction.details || '');
                      setIsEditing(false);
                    } else {
                      setIsEditing(true);
                    }
                  }}
                >
                  <LuPen className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-2 text-rose-300 hover:bg-rose-500/20"
                  aria-label="Delete interaction"
                  onClick={() => setPendingDelete(selectedInteraction)}
                >
                  <LuTrash2 className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-white/10 bg-white/5 p-2 text-slate-200 hover:bg-white/10"
                  aria-label="Close"
                  onClick={() => {
                    if (!isSaving) {
                      setSelectedInteraction(null);
                      setIsEditing(false);
                    }
                  }}
                >
                  <LuX className="h-4 w-4" />
                </button>
              </div>
            </div>

            {isEditing ? (
              <div className="space-y-3">
                <textarea
                  value={editedSummary}
                  onChange={(e) => setEditedSummary(e.target.value)}
                  className="h-40 w-full rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-slate-100 outline-none focus:border-sky-400/60 focus:ring-2 focus:ring-sky-400/30"
                  placeholder="Interaction summary"
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-white/10"
                    onClick={() => {
                      setEditedSummary(selectedInteraction.details || '');
                      setIsEditing(false);
                    }}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-[#050815] shadow-lg shadow-sky-500/30 disabled:opacity-60"
                    disabled={isSaving}
                    onClick={async () => {
                      setIsSaving(true);
                      try {
                        await updateInteraction(accessToken, selectedInteraction.id, {
                          details: editedSummary.trim(),
                        });
                        queryClient.invalidateQueries({ queryKey: ['contact', id] });
                        setSelectedInteraction((prev) =>
                          prev ? { ...prev, details: editedSummary.trim() } : prev
                        );
                        setIsEditing(false);
                      } finally {
                        setIsSaving(false);
                      }
                    }}
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <p className="whitespace-pre-line text-base leading-relaxed text-slate-100">
                {selectedInteraction.details || 'No details provided'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RelationshipInteractionsPage;
